import { graphql } from 'gatsby';
import Image from 'gatsby-image';
import React from 'react';
import { Button } from '../components/button';
import { CustomProductBookingForm } from '../components/custom-product-booking-form';
import { Dialog } from '../components/dialog';
import { useLocalize } from '../components/localize-context';
import { Seo } from '../components/seo';
import { VisitorContext } from '../components/visitor-context';
import { VisitorTypeSwitcher } from '../components/visitor-type-switcher';

export default function ProductPage({
  data: {
    prismicProductPage: { data },
  },
}) {
  return (
    <>
      <Seo title={data.title.text} />
      {data.title.text && (
        <div className="container mx-auto px-3">
          <h1 className="text-3xl md:text-5xl text-blue-700 p-2">
            {data.title.text}
          </h1>
        </div>
      )}
      {data.body.map((slice, index) =>
        slice.type === 'PrismicProductPageBodyImageWithDescription' ? (
          <ProductPageImageWithDescription key={index} {...slice} />
        ) : (
          <ProductPagePricing
            key={index}
            {...slice}
            productName={data.title.text}
          />
        )
      )}
    </>
  );
}

export const pageQuery = graphql`
  query ProductPage($id: String!) {
    prismicProductPage(id: { eq: $id }) {
      data {
        title {
          text
        }
        body {
          type: __typename
          ... on PrismicProductPageBodyImageWithDescription {
            primary {
              desc {
                html
              }
              image {
                alt
                localFile {
                  childImageSharp {
                    fluid(maxWidth: 1500, quality: 100) {
                      ...GatsbyImageSharpFluid_withWebp
                    }
                  }
                }
                wide {
                  localFile {
                    childImageSharp {
                      fluid(maxWidth: 1500, quality: 100) {
                        ...GatsbyImageSharpFluid_withWebp
                      }
                    }
                  }
                }
                small: tall {
                  localFile {
                    childImageSharp {
                      fluid(maxWidth: 450, quality: 100) {
                        ...GatsbyImageSharpFluid_withWebp
                      }
                    }
                  }
                }
                tall {
                  localFile {
                    childImageSharp {
                      fluid(maxWidth: 768, quality: 100) {
                        ...GatsbyImageSharpFluid_withWebp
                      }
                    }
                  }
                }
              }
            }
          }
          ... on PrismicProductPageBodyPricing {
            items {
              description: item_description {
                text
              }
              subtext: item_subtext {
                text
              }
              customer_type
              price
              afterPrice: price_append {
                text
              }
              unit {
                text
              }
            }
          }
        }
      }
    }
  }
`;

function ProductPageImageWithDescription({ primary }) {
  return (
    <section>
      {primary.image.localFile && (
        <>
          <div className="hidden md:block">
            <Image
              fluid={
                primary.image.wide.localFile
                  ? primary.image.wide.localFile.childImageSharp.fluid
                  : primary.image.localFile.childImageSharp.fluid
              }
              alt={primary.image.alt}
            />
          </div>
          <div className="hidden xs:block md:hidden">
            <Image
              fluid={
                primary.image.tall.localFile
                  ? primary.image.tall.localFile.childImageSharp.fluid
                  : primary.image.localFile.childImageSharp.fluid
              }
              alt={primary.image.alt}
            />
          </div>
          <div className="xs:hidden">
            <Image
              fluid={
                primary.image.small.localFile
                  ? primary.image.small.localFile.childImageSharp.fluid
                  : primary.image.localFile.childImageSharp.fluid
              }
              alt={primary.image.alt}
            />
          </div>
        </>
      )}
      {primary.desc.html && (
        <div className="container mx-auto max-w-lg my-6 px-4">
          <div
            className="rich-text text-lg"
            dangerouslySetInnerHTML={{ __html: primary.desc.html }}
          />
        </div>
      )}
    </section>
  );
}

function ProductPagePricing({ productName, items }) {
  const localize = useLocalize();
  const [visitorType] = React.useContext(VisitorContext);
  const [showForm, setShowForm] = React.useState(false);
  const displayedItems = items.filter(
    item =>
      item.customer_type ===
      (visitorType === 'local' ? 'local' : 'international')
  );

  return (
    <section className="container mx-auto px-3 py-12" id="pricing">
      <VisitorTypeSwitcher />
      <div className="container mx-auto p-3 max-w-sm">
        <ul>
          {displayedItems.map((item, index) => (
            <li className="flex justify-around items-center py-2" key={index}>
              <div className="text-2xl text-right">
                {item.description.text}
                {item.subtext.text && (
                  <div className="text-lg">{item.subtext.text}</div>
                )}
              </div>
              <div>
                <small className="text-base md:text-lg pr-1">
                  {localize('RM')}
                </small>
                <span className="text-3xl">{item.price}</span>
                <small className="text-lg ml-1">{item.afterPrice.text}</small>
              </div>
            </li>
          ))}
        </ul>
        <div className="text-center py-8">
          <Button
            onClick={() => setShowForm(true)}
            variant="primary"
            className="w-32 text-2xl"
          >
            {localize('Book')}
          </Button>
        </div>
        <Dialog
          title={`${localize('Book')} ${productName}`}
          isOpen={showForm}
          onDismiss={() => setShowForm(false)}
          fitContent
        >
          {showForm && (
            <div className="max-w-md mx-auto">
              <CustomProductBookingForm
                productName={productName}
                items={displayedItems}
              />
            </div>
          )}
        </Dialog>
      </div>
    </section>
  );
}
